import React from "react";
import { Link } from "react-router-dom";
import { LogOut, User, MessageCircle } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useUserProfile } from "@/hooks/useUserProfile";
import { supabase } from "@/integrations/supabase/client";

interface UserProfileSectionProps {
  role: string | null;
  onLogout: () => void;
}

export const UserProfileSection: React.FC<UserProfileSectionProps> = ({ role, onLogout }) => {
  const { profile, loading } = useUserProfile();

  // Função para obter as iniciais do nome
  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map(word => word.charAt(0))
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  // Função para obter o label do role
  const getRoleLabel = (userRole: string) => {
    switch (userRole) {
      case "admin":
        return "Administrador";
      case "mentor":
        return "Mentor";
      case "jogador":
        return "Jogador";
      default:
        return "Usuário";
    }
  };

  const getProfileUrl = (role: string) => {
    if (role === "admin") return "/painel-admin";
    if (role === "mentor") return "/painel-mentor/perfil";
    return "/painel-jogador/perfil";
  };

  if (loading || !profile) {
    return (
      <div className="flex items-center gap-2">
        <div className="h-8 w-8 bg-[#242C37] rounded-full animate-pulse"></div>
        <div className="hidden md:block">
          <div className="h-4 w-20 bg-[#242C37] rounded animate-pulse"></div>
        </div>
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          className="flex items-center gap-2 hover:bg-[#242C37] p-2 rounded-lg transition-colors"
        >
          <Avatar className="h-8 w-8">
            <AvatarImage 
              src={profile.image_url || ""} 
              alt={profile.nickname || "Avatar"} 
            />
            <AvatarFallback className="bg-[#00B6F0] text-black text-sm font-bold">
              {profile.nickname ? getInitials(profile.nickname) : "U"}
            </AvatarFallback>
          </Avatar>
          <div className="hidden md:block text-left">
            <p className="text-sm font-medium text-white leading-none">
              {profile.nickname}
            </p>
            <p className="text-xs text-gray-400 mt-1">
              {getRoleLabel(profile.role)}
            </p>
          </div>
        </Button>
      </DropdownMenuTrigger>
      
      <DropdownMenuContent 
        align="end" 
        className="w-56 bg-[#1A2233] border-[#2D3748] text-white"
      >
        <div className="px-2 py-1.5">
          <p className="text-sm font-medium">{profile.nickname}</p>
          <p className="text-xs text-gray-400">{getRoleLabel(profile.role)}</p>
        </div>
        
        <DropdownMenuSeparator className="bg-[#2D3748]" />
        
        <DropdownMenuItem asChild>
          <Link 
            to={getProfileUrl(role || "")}
            className="flex items-center gap-2 cursor-pointer hover:bg-[#242C37] focus:bg-[#242C37]"
          >
            <User size={16} />
            Meu Perfil
          </Link>
        </DropdownMenuItem>
        
        <DropdownMenuSeparator className="bg-[#2D3748]" />
        
        <DropdownMenuItem asChild>
          <button
            onClick={() => {
              const whatsappUrl = 'https://wa.me/5511943006646?text=Ol%C3%A1%2C%20gostaria%20de%20ajuda%20com%20a%20Arena%20Pro%20Player...';
              window.open(whatsappUrl, '_blank');
            }}
            className="flex items-center gap-2 cursor-pointer hover:bg-[#25D366] focus:bg-[#25D366] w-full text-left"
          >
            <MessageCircle size={16} />
            Fale Conosco
          </button>
        </DropdownMenuItem>
        
        <DropdownMenuSeparator className="bg-[#2D3748]" />
        
        <DropdownMenuItem 
          onClick={onLogout}
          className="flex items-center gap-2 cursor-pointer hover:bg-[#FF5555] focus:bg-[#FF5555] text-red-400 hover:text-white focus:text-white"
        >
          <LogOut size={16} />
          Sair
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}; 